import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { extractNationalNumber, isSamePhoneNumberValue } from 'utils/phoneUtils';
import { findStateByCodeAndCountry } from 'utils/addressUtils';
import { extractCustomFieldCodes, formatBackendValidationErrors, getBackendErrorsFromResponse } from 'utils';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import store from 'modules/state-mgmt/store';
import { updateWorker, setOnboarding } from 'modules/state-mgmt/onboarding';
import useForm from 'hooks/useForm';
import { WorkerProfileField } from 'types/profileCustomFields';
import { actionMapForOtherLoaders } from 'constants/actions';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard/hooks/useWizardStepNavigation';
import { CONTACT_INFO_FIELD_CONFIG } from 'hooks/useContactInformationFields';
import { ROUTES } from 'constants/routes';

import { IContactFormState } from '../types';
import contactInformationSchema from '../schema';
import { useContactSectionsConfig } from './useSectionsConfig';

const FIELD_TO_FORM_KEY_MAP: Partial<Record<WorkerProfileField, keyof IContactFormState>> = {
  [WorkerProfileField.email]: 'email',
  [WorkerProfileField.mobilePhoneNumber]: 'mobileNumber',
  [WorkerProfileField.alternateContactMobilePhoneNumber]: 'phoneNumber',
  [WorkerProfileField.homeAddressLine1]: 'addressLine1',
  [WorkerProfileField.homeAddressLine2]: 'addressLine2',
  [WorkerProfileField.homeAddressCountry]: 'country',
  [WorkerProfileField.homeAddressStateProvince]: 'state',
  [WorkerProfileField.homeAddressCity]: 'city',
  [WorkerProfileField.homeAddressZipPostal]: 'zipCode',
  [WorkerProfileField.emergencyContactName]: 'emergencyContactName',
  [WorkerProfileField.emergencyContactRelationship]: 'emergencyContactRelationship',
  [WorkerProfileField.emergencyContactMobilePhone]: 'emergencyContactNumber',
};

interface IUseContactFormReturn {
  // Form state and handlers from useForm
  contactForm: IContactFormState;
  formErrors: Record<string, { message: string; type?: string }>;
  setValue: <K extends keyof IContactFormState>(key: K, value: IContactFormState[K]) => void;
  setErrors: (errors: Record<string, { message: string; type?: string }>) => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;

  // Field change handlers
  handleFieldChange: (field: WorkerProfileField, value: string) => void;
  handlePhoneChange: (
    field: WorkerProfileField,
  ) => (value: { phone: string; inputValue: string; country: any }) => void;
  handleDirectFieldUpdate: (fieldName: keyof IContactFormState, value: string) => void;

  // Edit permissions
  canEditEmail: boolean;
  canEditMobilePhoneNumber: boolean;

  isSubmitting: boolean;
}

export const useContactForm = (): IUseContactFormReturn => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { goToNextStep } = useWizardStepNavigation();

  // Get workerId and language from Redux state
  const { workerId, languageCode, profileFieldConstraintsMap, isOffline, isWorker, uploadingDetails } = useAppSelector(
    state => ({
      workerId: state.onboarding.id,
      languageCode: state.app?.languageCode,
      profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
      isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
      isWorker: !state.user?.userId || state.user?.isWorker,
      uploadingDetails: state.onboarding?.uploadingDetails,
    }),
  );

  const {
    email,
    mobilePhoneNumber,
    phoneNumber,
    address,
    emergencyContactName,
    emergencyContactPhone,
    emergencyContactRelationship,
    canEditEmail,
    canEditMobilePhoneNumber,
    hasReachedConsentForm,
    isUpdatingWorker,
    isArchivedProject,
  } = useAppSelector(state => ({
    email: state.onboarding.email || '',
    mobilePhoneNumber: state.onboarding.mobilePhoneNumber || '',
    phoneNumber: state.onboarding.phoneNumber || '',
    address: state.onboarding.address || {},
    emergencyContactName: state.onboarding.emergencyContactName || '',
    emergencyContactPhone: state.onboarding.emergencyContactPhone || '',
    emergencyContactRelationship: state.onboarding.emergencyContactRelationship || '',
    canEditEmail: state.onboarding.canEditEmail || false,
    canEditMobilePhoneNumber: state.onboarding.canEditMobilePhoneNumber || false,
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm || false,
    isUpdatingWorker: state.loader.otherLoaders?.[actionMapForOtherLoaders.updateWorker]?.isLoading || false,
    isArchivedProject: state.cache.project.status === 3,
  }));

  // Initial form state derived from Redux store (read-only)
  const initialValues: IContactFormState = useMemo(() => {
    return {
      email,
      mobileNumber: mobilePhoneNumber,
      phoneNumber: phoneNumber,
      emergencyContactNumber: emergencyContactPhone || '',
      addressLine1: address.line1 || '',
      addressLine2: address.line2 || '',
      country: address?.country?.id || '',
      countryName: address?.country?.name || '',
      state: address?.stateCode || '',
      stateName: address?.stateName || '',
      city: address.city || '',
      zipCode: address.zipCode || '',
      county: address.county || '',
      borough: address.borough || '',
      latitude: address.latitude,
      longitude: address.longitude,
      emergencyContactName: emergencyContactName || '',
      emergencyContactRelationship: emergencyContactRelationship || '',
    };
  }, [
    email,
    mobilePhoneNumber,
    phoneNumber,
    address,
    emergencyContactName,
    emergencyContactPhone,
    emergencyContactRelationship,
  ]);

  // Create basic sections config for validation (without form values)
  const sectionsConfig = useContactSectionsConfig();

  const hasPendingUploadErrorForPage = uploadingDetails?.errors?.pageUrl === ROUTES.CONTACT_INFORMATION.path;

  const {
    values: contactForm,
    errors: formErrors,
    setValue,
    setErrors,
    handleSubmit,
    getIsDirty,
  } = useForm({
    initialValues,
    validationSchema: contactInformationSchema(sectionsConfig, t, profileFieldConstraintsMap, hasReachedConsentForm),
    ignoreValidations: isArchivedProject,
    onSubmit: handleFormSubmit,
  });

  // Separate handleFormSubmit function for better organization
  async function handleFormSubmit(values: IContactFormState) {
    // Nothing was edited on this page — no worker update needed, online or offline.
    // A failed background upload for this page still needs a retry, so it forces the write.
    if (!getIsDirty() && !hasPendingUploadErrorForPage) {
      goToNextStep();
      return;
    }

    if (!workerId && !uploadingDetails?.isOffline && !isWorker) {
      enqueueSnackbar('Error: Worker ID not available', { variant: 'error' });
      return;
    }
    if (isArchivedProject) {
      goToNextStep();
      return;
    }

    // Helper function to validate phone numbers - only send if user has typed something
    const getValidPhoneNumber = (phoneValue: string): string => {
      const nationalNumber = extractNationalNumber(phoneValue);
      return nationalNumber && nationalNumber.trim().length > 0 ? phoneValue : null;
    };

    try {
      // Get cache data to build proper country/state objects
      const cacheState = store.getState().cache;
      const currentLanguageCountries = cacheState?.countries?.[languageCode] || [];
      const currentLanguageStates = cacheState?.states?.[languageCode] || [];

      // Find country and state objects from cache
      const selectedCountry = currentLanguageCountries.find((c: any) => c.id === values.country);
      const selectedState = findStateByCodeAndCountry(currentLanguageStates, values.state, selectedCountry);

      const apiData = {
        // Primary Contact Information
        email: values.email || null,
        mobilePhoneNumber: getValidPhoneNumber(values.mobileNumber),
        phoneNumber: getValidPhoneNumber(values.phoneNumber),

        address: {
          line1: values.addressLine1,
          line2: values.addressLine2,
          city: values.city,
          zipCode: values.zipCode,
          county: values.county,
          borough: values.borough,
          latitude: values.latitude,
          longitude: values.longitude,
          countryId: selectedCountry?.id || '',
          country: selectedCountry
            ? {
                id: selectedCountry.id,
                name: selectedCountry.name,
                code: selectedCountry.code,
              }
            : null,
          stateCode: selectedState ? selectedState.code : values.state || '',
          stateName: selectedState ? selectedState.name : values.stateName || '',
        },

        emergencyContactName: values.emergencyContactName,
        emergencyContactPhone: getValidPhoneNumber(values.emergencyContactNumber),
        emergencyContactRelationship: values.emergencyContactRelationship,
      };

      if (isOffline && !isWorker) {
        // Explicit Offline Flow (Middleware handles persistence)
        await dispatch(
          setOnboarding({
            ...apiData,
            uploadingDetails: {
              ...(uploadingDetails || {}),
              isOffline: true,
              pendingUploads: {
                ...(uploadingDetails?.pendingUploads || {}),
                contactInformation: true,
              },
              errors:
                uploadingDetails?.errors?.pageUrl === ROUTES.CONTACT_INFORMATION.path
                  ? { pageUrl: '', errorObject: null }
                  : uploadingDetails?.errors,
            },
          }),
        );

        goToNextStep();
        return;
      }

      // Extract custom field codes for Contact Information screen
      const workerCustomFieldCodesToValidate = extractCustomFieldCodes(CONTACT_INFO_FIELD_CONFIG);

      const res = await dispatch(
        updateWorker({
          workerId,
          data: apiData,
          languageCode,
          workerCustomFieldCodesToValidate,
        }),
      );

      if (res.type.includes('fulfilled')) {
        goToNextStep();
      } else {
        // Handle backend validation errors - show all errors in snackbar
        const errorMessage = formatBackendValidationErrors(res.payload);
        enqueueSnackbar('Failed to update contact information. Please try again.', { variant: 'error' });
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
      }
    } catch (error) {
      enqueueSnackbar('An error occurred while updating contact information.', { variant: 'error' });
    }
  }

  // Simple field change handler - only updates local form state
  const handleFieldChange = useCallback(
    (field: WorkerProfileField, value: string) => {
      const formField = FIELD_TO_FORM_KEY_MAP[field];
      if (formField) {
        setValue(formField, value);
      }
    },
    [setValue],
  );

  const handlePhoneChange = useCallback(
    (field: WorkerProfileField) => (value: { phone: string; inputValue: string; country: any }) => {
      const formField = FIELD_TO_FORM_KEY_MAP[field];

      // MobileInput's usePhoneInput re-formats the value it is handed and fires onChange on mount
      // when its output differs from that value ('' becomes '+1', a stored number comes back
      // spaced). That is not a user edit, so drop updates that would not change the payload —
      // otherwise merely opening this page marks the form dirty and triggers a pointless
      // updateWorker call. A cleared field or a country switch still gets through.
      if (formField && isSamePhoneNumberValue(value.phone, (contactForm[formField] as string) || '', value.country)) {
        return;
      }

      // Store only the full phone number
      handleFieldChange(field, value.phone);
    },
    [handleFieldChange, contactForm],
  );

  // Direct field updater for fields that don't have WorkerProfileField enum (like county)
  const handleDirectFieldUpdate = useCallback(
    (fieldName: keyof IContactFormState, value: string) => {
      setValue(fieldName, value);
    },
    [setValue],
  );

  // On mount, populate form errors from background upload errors (if any)
  useEffect(() => {
    const uploadErrors = uploadingDetails?.errors;
    if (uploadErrors?.pageUrl === ROUTES.CONTACT_INFORMATION.path && uploadErrors?.errorObject) {
      const contactFields = [
        'email',
        'mobilePhoneNumber',
        'phoneNumber',
        'addressLine1',
        'addressLine2',
        'country',
        'state',
        'city',
        'zipCode',
        'county',
        'borough',
        'emergencyContactName',
        'emergencyContactPhone',
        'emergencyContactRelationship',
      ];
      const errorFieldMapper = {
        email: 'contactEmail',
        mobilePhoneNumber: 'contactMobilePhoneNumber',
        phoneNumber: 'ContactAlternateMobileNumber',
        addressLine1: 'homeAddressLine1',
        addressLine2: 'homeAddressLine2',
        country: 'homeAddressCountry',
        state: 'homeAddressStateProvince',
        city: 'homeAddressCity',
        zipCode: 'homeAddressZipPostal',
        county: 'homeAddressCounty',
        borough: 'homeAddressBorough',
      };
      const backendErrors = getBackendErrorsFromResponse(uploadErrors.errorObject, contactFields, errorFieldMapper);
      if (backendErrors) {
        setErrors(backendErrors);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Form state and handlers from useForm
    contactForm,
    formErrors,
    setValue,
    setErrors,
    handleSubmit,

    // Field change handlers
    handleFieldChange,
    handlePhoneChange,
    handleDirectFieldUpdate,

    // Edit permissions
    canEditEmail,
    canEditMobilePhoneNumber,
    isSubmitting: isUpdatingWorker,
  };
};
